import Joi from "joi";
import { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getColleges,
  getUserTypes,
  getUserPageBlocks,
  getCollegeDepartments,
} from "../../../../../action/common/commonActionCreator";
import { createMediaContent } from "../../../../../action/masters/mediaContentActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ConfigUserTypes,
  UserTypes,
} from "../../../../../models/dashboard/adminDashboard";
import {
  ICreateMediaContent,
  IMediaContentData,
} from "../../../../../models/master/mediaContent";
import { booleanOptions } from "../../../../../constants/commonConstants";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";

const CreateMediaContent = (props: ICreateMediaContent) => {
  const {
    createMediaContent,
    setShowCreate,
    getColleges,
    colleges,
    getUserPageBlocks,
    userTypes,
    getUserTypes,
    pageBlocks,
    getAllMediaContents,
    getCollegeDepartments,
    collegeDepartments,
  } = props;
  const navigate = useNavigate();

  const initialData: IMediaContentData = {
    id: null,
    title: "",
    file: null,
    userType: UserTypes.Staff,
    url: "",
    sso: "",
    isActive: true,
    priority: 0,
    collegeIds: [],
    pageBlockIds: [],
    departmentIds: [],
  };

  // states

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const ssoOptionsList = Object.entries(booleanOptions).map(([key, value]) => ({
    id: value,
    name: key,
  }));
  const [isSSOEnabled, setIsSSOEnabled] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false)

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value));
    }
    if (event.target.name === "collegeIds") {
      getCollegeDepartments(_data.collegeIds.toString());
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const fileType = selectedFile.type;
      const validFileTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/ico", "image/svg+xml"];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      }
      else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const urlRegex = /^https?:\/\/([a-z0-9]{2,}(-[a-z0-9]{2,})*\.)+[a-z0-9]{2,6}(\/[a-z0-9#?=&%\/\-_\.]*)?$/i;
  const schema = Joi.object().keys({
    title: Joi.string().required().label("Title is required"),
    userType: Joi.number().greater(0).required().label("Select UserType"),
    url: Joi.string().required().regex(urlRegex).label("Enter Valid URL"),
    file: Joi.object().required().label("File is required"),
    pageBlockIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Page Block"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select College"),
    departmentIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select College Department"),
    sso: Joi.string().when("isSSOEnabled", {
      is: true,
      then: Joi.string().required().regex(urlRegex).label("Enter Valid SSO"),
      otherwise: Joi.string().allow("", null).optional(),
    }),
  });

  const validationData = {
    ...data,
    isSSOEnabled: isSSOEnabled,
  };

  const onSave = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("title", data.title);
      formData.append("userType", data.userType?.toString() || "");
      formData.append("file", data.file);
      formData.append("url", data.url);
      formData.append("sso", data.sso);
      formData.append("isActive", data.isActive.toString());
      formData.append("priority", data.priority.toString());
      data.collegeIds.map((item, index) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });
      data.pageBlockIds.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.departmentIds.map((item, index) => {
        formData.append(`departmentIds[${index}]`, item.toString());
      });

      setLoading(true)
      setIsDisabled(true)
      try {
        await createMediaContent(formData, setAlert);
      } finally {
        setLoading(false)
        setIsDisabled(false)
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllMediaContents();
      setShowCreate(false);
    }
  };

  //setting warning alert on cancel
  const onCancel = async () => {
    setShowAlertCancel(true);
    setAlert(alertConstants.cancelCheck);
  };

  useEffect(() => {
    getColleges().then(() => {
      setData((data) => ({
        ...data,
        collegeIds: colleges.map((college) => college.id),
      }));
    });
    getUserTypes();
    getUserPageBlocks(ConfigUserTypes.Staff);
  }, []);

  useEffect(() => {
    if (data.collegeIds.length > 0) {
      getCollegeDepartments(data.collegeIds.toString());
    }
  }, [data.collegeIds]);

  useEffect(() => {
    if (
      collegeDepartments &&
      data.collegeIds &&
      collegeDepartments.length > 0 &&
      data.collegeIds.length > 0
    ) {
      setData((prevData) => ({
        ...prevData,
        departmentIds: collegeDepartments.map((dept) => dept.id),
      }));
    }
  }, [collegeDepartments, data.collegeIds]);
  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Add New Media Content
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowCreate(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-12 form-group">
            <FloatingLabelDropDown
              label={"User Type "}
              onChange={onChange}
              isMandatory={true}
              value={data.userType}
              name={"userType"}
              disable
              optionsList={sortAlphabetically(userTypes)}
            />
            {errors?.userType && (
              <div className="text-danger">{errors.userType}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Title "}
              onChange={onChange}
              isMandatory={true}
              value={data.title}
              name={"title"}
              maxLength={50}
            />
            {errors?.title && <div className="text-danger">{errors.title}</div>}
          </div>
          <div className="col-md-6 form-group">
            <div className="d-flex align-items-end gap-2">
              <div className="w-100 inputfile-wrap">
                <label className="profile_details_text">
                  Icon<i className="label-asterisk"> * </i>
                </label>
                <input
                  type="file"
                  name="file"
                  className="form-control"
                  required
                  onChange={onFileChange}
                  value={errors?.file && ''}
                />
              </div>
              {imagePreview && (
                <div className="">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: "120px", maxHeight: "50px" }}
                  />
                </div>
              )}
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"URL "}
              onChange={onChange}
              isMandatory={true}
              value={data.url}
              name={"url"}
              maxLength={500}
            />
            {errors?.url && <div className="text-danger">{errors.url}</div>}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Is SSO Enabled "}
              onChange={(e: any) => setIsSSOEnabled(e.target.value)}
              isMandatory={true}
              value={isSSOEnabled}
              name={"isSSOEnabled"}
              optionsList={ssoOptionsList}
            />
          </div>
          {isSSOEnabled && (
            <div className="col-md-6 form-group">
              <FloatingLabelTextInput
                label={"SSO"}
                onChange={onChange}
                isMandatory={true}
                value={data.sso}
                name={"sso"}
                maxLength={500}
              />
              {errors?.sso && <div className="text-danger">{errors.sso}</div>}
            </div>
          )}
          <div className="col-md-6 form-group">
            <MultipleSelect
              label={"Page-Block "}
              onChange={onChange}
              isMandatory={true}
              values={data.pageBlockIds as number[]}
              name={"pageBlockIds"}
              optionList={sortAlphabetically(pageBlocks)}
            />
            {errors?.pageBlockIds && (
              <div className="text-danger">{errors.pageBlockIds}</div>
            )}
          </div>
          <div
            className={
              isSSOEnabled === true
                ? "col-md-12 form-group"
                : "col-md-6 form-group"
            }
          >
            <MultipleSelect
              label={"College "}
              onChange={onChange}
              isMandatory={true}
              disabled={true}
              values={data.collegeIds as number[]}
              name={"collegeIds"}
              optionList={sortAlphabetically(colleges)}
            />
            {errors?.collegeIds && (
              <div className="text-danger">{errors.collegeIds}</div>
            )}
          </div>
          <div className="col-md-12 form-group">
            <MultipleSelect
              label={"Department Colleges "}
              onChange={onChange}
              isMandatory={true}
              disabled={true}
              values={data.departmentIds as number[]}
              name={"departmentIds"}
              optionList={sortAlphabetically(collegeDepartments)}
            />
            {errors?.departmentIds && (
              <div className="text-danger">{errors.departmentIds}</div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-primary" onClick={onSave} disabled={isDisabled}>
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowCreate(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && (<LoaderSpinner />)}
    </>
  );
};

const mapDispatchToProps = {
  createMediaContent: (data: any, callBack: Function) =>
    createMediaContent(data, callBack),
  getColleges: () => getColleges(),
  getUserTypes: () => getUserTypes(),
  getUserPageBlocks: (userType: number) => getUserPageBlocks(userType),
  getCollegeDepartments: (collegeIds: string) =>
    getCollegeDepartments(collegeIds),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
    collegeDepartments: state.common.collegeDepartments,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateMediaContent);
